import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from '../../../axiosConfig';
import styles from '../MicroProbe/Companies/CustomDropdownFinancialData.module.css';

const CryptoDropdown = ({ onSelect, label }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [fromQuery, setFromQuery] = useState('');
  const [toQuery, setToQuery] = useState('');
  const [fromSuggestions, setFromSuggestions] = useState([]);
  const [toSuggestions, setToSuggestions] = useState([]);
  const [fromLoading, setFromLoading] = useState(false);
  const [toLoading, setToLoading] = useState(false);
  const [selectedFromCrypto, setSelectedFromCrypto] = useState('');
  const [selectedToMarket, setSelectedToMarket] = useState('');
  const [selectedPair, setSelectedPair] = useState(''); // Add this to track the selected pair
  const dropdownRef = useRef(null);

  const loadCryptoSuggestions = async (query, setSuggestions, setLoading) => {
    if (query.length > 1) {
      setLoading(true);
      try {
        const response = await axios.get(`/api/cryptosearch/?q=${query}`);
        setSuggestions(response.data);
      } catch (error) {
        console.error('Error fetching crypto suggestions:', error);
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    } else {
      setSuggestions([]);
    }
  };

  const loadFxSuggestions = async (query, setSuggestions, setLoading) => {
    if (query.length > 0) {
      setLoading(true);
      try {
        const response = await axios.get(`/api/fxsearch/?q=${query}`);
        setSuggestions(response.data);
      } catch (error) {
        console.error('Error fetching fx suggestions:', error);
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    } else {
      setSuggestions([]);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => loadCryptoSuggestions(fromQuery, setFromSuggestions, setFromLoading), 300);
    return () => clearTimeout(timeoutId);
  }, [fromQuery]);

  useEffect(() => {
    const timeoutId = setTimeout(() => loadFxSuggestions(toQuery, setToSuggestions, setToLoading), 300);
    return () => clearTimeout(timeoutId);
  }, [toQuery]);

  const toggleDropdown = () => {
    if (!isOpen && selectedPair) {
      // If reopening and a pair is already selected, clear the previous selection
      setSelectedPair('');
      setSelectedFromCrypto('');
      setSelectedToMarket('');
      setFromQuery('');
      setToQuery('');
      setFromSuggestions([]);
      setToSuggestions([]);
    }
    setIsOpen(!isOpen);
  };

  const handleFromCryptoSelect = (crypto) => {
    const newFromCrypto = crypto.code;
    setSelectedFromCrypto(newFromCrypto);
    setFromQuery(crypto.name);
    setFromSuggestions([]);
    // Only check if we already have a "To Market" selected
    if (selectedToMarket) {
      checkAndSubmitPair(newFromCrypto, selectedToMarket);
    }
  };

  const handleToMarketSelect = (market) => {
    const newToMarket = market.code;
    setSelectedToMarket(newToMarket);
    setToQuery(market.name);
    setToSuggestions([]);
    // Only check if we already have a "From Crypto" selected
    if (selectedFromCrypto) {
      checkAndSubmitPair(selectedFromCrypto, newToMarket);
    }
  };

  const checkAndSubmitPair = (fromCode, toCode) => {
    if (fromCode && toCode) {
      const pairName = `${fromCode}/${toCode}`;
      const cryptoData = {
        name: pairName,
        code: pairName,
        type: 'crypto'
      };
      onSelect(cryptoData.code, cryptoData);
      
      // Set the selected pair for display but don't reset the selections immediately
      setSelectedPair(pairName);
      setIsOpen(false);
    }
    // Don't close dropdown if only one crypto is selected
  };

  const handleClickOutside = useCallback((event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsOpen(false);
      // Clear suggestions when clicking outside
      setFromSuggestions([]);
      setToSuggestions([]);
      // Only reset partial selections if no pair has been selected yet
      if (!selectedPair) {
        setSelectedFromCrypto('');
        setSelectedToMarket('');
        setFromQuery('');
        setToQuery('');
      }
    }
  }, [selectedPair]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  return (
    <div className={styles.customDropdown} ref={dropdownRef}>
      <div 
        className={styles.selected} 
        onClick={toggleDropdown}
        style={{ backgroundColor: '#cce7f0' }}
      >
        <span>{selectedPair || label}</span>
        <span className={`${styles.arrow} ${isOpen ? styles.open : ''}`} />
      </div>
      {isOpen && (
        <div className={styles.dropdownMenu} style={{ maxHeight: '400px', overflowY: 'auto' }}>
          <div style={{ padding: '10px', borderBottom: '1px solid #eee' }}>
            {/* From Crypto Section */}
            <div style={{ marginBottom: '15px' }}>
              <label style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>
                From Crypto:
              </label>
              <input
                type="text"
                placeholder="Search cryptocurrency..."
                value={fromQuery}
                onChange={(e) => setFromQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
              {fromLoading && (
                <div style={{ padding: '5px', fontSize: '12px', color: '#666' }}>
                  Loading...
                </div>
              )}
              {!fromLoading && fromSuggestions.length > 0 && (
                <div style={{ maxHeight: '100px', overflowY: 'auto', border: '1px solid #ddd', borderTop: 'none' }}>
                  {fromSuggestions.slice(0, 5).map((suggestion, index) => (
                    <div
                      key={suggestion.id || index}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleFromCryptoSelect(suggestion);
                      }}
                      style={{ 
                        padding: '5px 8px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #f0f0f0',
                        fontSize: '13px',
                        backgroundColor: '#fff'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = '#fff'}
                    >
                      <div style={{ fontWeight: 'bold' }}>{suggestion.name}</div>
                      <div style={{ fontSize: '11px', color: '#666' }}>
                        {suggestion.code}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* To Market Section */}
            <div style={{ marginBottom: '10px' }}>
              <label style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>
                To Market:
              </label>
              <input
                type="text"
                placeholder="Search market (USD, EUR, etc.)..."
                value={toQuery}
                onChange={(e) => setToQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
              {toLoading && (
                <div style={{ padding: '5px', fontSize: '12px', color: '#666' }}>
                  Loading...
                </div>
              )}
              {!toLoading && toSuggestions.length > 0 && (
                <div style={{ maxHeight: '100px', overflowY: 'auto', border: '1px solid #ddd', borderTop: 'none' }}>
                  {toSuggestions.slice(0, 5).map((suggestion, index) => (
                    <div
                      key={suggestion.id || index}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToMarketSelect(suggestion);
                      }}
                      style={{ 
                        padding: '5px 8px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #f0f0f0',
                        fontSize: '13px',
                        backgroundColor: '#fff'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = '#fff'}
                    >
                      <div style={{ fontWeight: 'bold' }}>{suggestion.name}</div>
                      <div style={{ fontSize: '11px', color: '#666' }}>
                        {suggestion.code}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Selected Pair Display */}
            {selectedFromCrypto && selectedToMarket && (
              <div style={{ 
                padding: '8px',
                backgroundColor: '#e8f5e8',
                border: '1px solid #28a745',
                borderRadius: '4px',
                fontSize: '13px',
                textAlign: 'center'
              }}>
                Selected: {selectedFromCrypto}/{selectedToMarket}
              </div>
            )}
            
            {/* Instruction */}
            <div style={{ 
              fontSize: '12px', 
              color: '#666', 
              textAlign: 'center',
              marginTop: '10px',
              fontStyle: 'italic'
            }}>
              Select crypto and market to add the pair
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CryptoDropdown;
